// @ts-nocheck
// CAMINHO: src/components/tasks/CaseTasks.tsx
// VERSÃO: FASE 3.1 - Criação do Módulo de Tarefas
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, PlusCircle, Trash2, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Task {
    id: string;
    created_at: string;
    description: string;
    is_completed: boolean;
    case_id: string;
}

interface CaseTasksProps {
    caseId: string;
}

export const CaseTasks = ({ caseId }: CaseTasksProps) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [newTaskDescription, setNewTaskDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .eq('case_id', caseId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setTasks(data || []);
        } catch (error: any) {
            toast({
                title: 'Erro ao buscar tarefas',
                description: error.message,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [caseId, toast]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handleAddTask = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTaskDescription.trim()) return;

        try {
            const { data, error } = await supabase
                .from('tasks')
                .insert({
                    description: newTaskDescription.trim(),
                    case_id: caseId,
                    is_completed: false,
                })
                .select()
                .single();

            if (error) throw error;
            
            setTasks(prevTasks => [data, ...prevTasks]);
            setNewTaskDescription('');
            toast({ title: 'Tarefa adicionada!' });
        } catch (error: any) {
            toast({
                title: 'Erro ao adicionar tarefa',
                description: error.message,
                variant: 'destructive',
            });
        }
    };

    const handleToggleComplete = async (task: Task) => {
        try {
            const { error } = await supabase
                .from('tasks')
                .update({ is_completed: !task.is_completed })
                .eq('id', task.id);

            if (error) throw error;

            setTasks(prevTasks =>
                prevTasks.map(t =>
                    t.id === task.id ? { ...t, is_completed: !t.is_completed } : t
                )
            );
        } catch (error: any) {
            toast({
                title: 'Erro ao atualizar tarefa',
                description: error.message,
                variant: 'destructive',
            });
        }
    };

    const handleDeleteTask = async (taskId: string) => {
        try {
            const { error } = await supabase.from('tasks').delete().eq('id', taskId);
            if (error) throw error;
            setTasks(prevTasks => prevTasks.filter(t => t.id !== taskId));
            toast({ title: 'Tarefa removida.' });
        } catch (error: any) {
            toast({
                title: 'Erro ao remover tarefa',
                description: error.message,
                variant: 'destructive',
            });
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> Tarefas do Processo
                </CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleAddTask} className="flex items-center gap-2 mb-4">
                    <Input
                        value={newTaskDescription}
                        onChange={(e) => setNewTaskDescription(e.target.value)}
                        placeholder="Adicionar nova tarefa..."
                    />
                    <Button type="submit" size="icon">
                        <PlusCircle className="w-4 h-4" />
                    </Button>
                </form>
                {loading ? (
                    <div className="flex justify-center items-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {tasks.length > 0 ? tasks.map(task => (
                            <div key={task.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50">
                                <Checkbox
                                    id={`task-${task.id}`}
                                    checked={task.is_completed}
                                    onCheckedChange={() => handleToggleComplete(task)}
                                />
                                <label
                                    htmlFor={`task-${task.id}`}
                                    className={`flex-1 text-sm ${task.is_completed ? 'line-through text-muted-foreground' : ''}`}
                                >
                                    {task.description}
                                </label>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteTask(task.id)}>
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                            </div>
                        )) : (
                            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma tarefa para este caso.</p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
